import { memo, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentViewer,
  MiButton,
  MiList,
  MiMenu,
  MiModalConfirm,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  MODAL_TITLES,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { formatDateIfValid } from "@miplatform/utils";
import { mdiOpenInNew, mdiTrashCanOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { PrebillPaymentInformationModal } from "../PrebillPaymentInformationModal/PrebillPaymentInformationModal";
import { PREBILL_PAYMENT_METHOD_LABEL_MAP } from "../prebillConstants";
import { usePrebillPaymentInformationViewModel } from "./usePrebillPaymentInformationViewModel";

export const PrebillPaymentInformation = memo(
  ({
    attempt,
    invoice,
    setBillingCommitmentsTotal,
    getInvoice,
    stateManager,
  }) => {
    const { theme, classes } = useStyles();
    const [
      isPaymentModalOpen,
      handlePaymentModalOpen,
      handlePaymentModalClose,
    ] = useBoolean();
    const [isDeleteModalOpen, handleDeleteModalOpen, handleDeleteModalClose] =
      useBoolean();
    const [selectedBillingCommitment, setSelectedBillingCommitment] =
      useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const {
      isLoading,
      billingCommitments,
      getBillingCommitments,
      deleteBillingCommitment,
    } = usePrebillPaymentInformationViewModel({
      attempt,
      stateManager,
    });

    useEffect(() => {
      if (!invoice) {
        getInvoice();
      }
    }, [invoice]);

    useEffect(() => {
      getBillingCommitments();
    }, []);

    useEffect(() => {
      setBillingCommitmentsTotal(
        billingCommitments.reduce((acc, curr) => acc + curr.amount, 0)
      );
    }, [billingCommitments]);

    const sections = [
      {
        fieldsetStyle: {
          justifyContent: "flex-end",
          alignItems: "center",
        },
        fieldset: [
          {
            field: "number",
            maxWidth: 400,
            justifyContent: "flex-end",
            renderCell: (row) => {
              return (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  gap={theme.spacing(2)}
                >
                  <MiStackedTypography
                    title="Date Added"
                    className={classes.fieldMargin}
                    titleProps={{
                      width: 70,
                    }}
                    description={formatDateIfValid(
                      row.modifiedOn,
                      DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
                    )}
                    descriptionProps={{
                      width: 100,
                    }}
                  />
                  <MiStackedTypography
                    title="Amount"
                    className={classes.fieldMargin}
                    titleProps={{
                      width: 70,
                    }}
                    description={Math.abs(row.amount).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                      currency: "USD",
                      style: "currency",
                    })}
                    descriptionProps={{ width: 70 }}
                  />
                </Box>
              );
            },
          },
        ],
      },
    ];

    const renderItemActions = (item) => (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu
          data={[
            {
              label: BUTTON_LABELS.VIEW_DOCUMENT,
              icon: mdiOpenInNew,
              action: () => setSelectedDocument(item.document),
              color: theme.palette.grey.medium,
            },
            {
              label: BUTTON_LABELS.DELETE,
              icon: mdiTrashCanOutline,
              action: () => {
                handleDeleteModalOpen();
                setSelectedBillingCommitment(item);
              },
              color: theme.palette.red.main,
            },
          ]}
        />
      </Box>
    );

    return (
      <Box className={classes.root}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.text}>Payment Information</Typography>
          <MiButton
            title="Add Payment Information"
            size="small"
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={handlePaymentModalOpen}
          />
        </Box>
        {billingCommitments.length > 0 && (
          <MiList
            hideListPagination
            viewType="card"
            cardProps={{
              sections,
              renderItemActions,
              containerStyles: {
                backgroundColor: theme.palette.common.white,
                boxShadow: "none",
              },
              renderTitle: (billing) => (
                <Box display="flex" flexDirection="column">
                  <Typography
                    color="primary.main"
                    fontWeight={600}
                    fontSize={14}
                  >
                    {PREBILL_PAYMENT_METHOD_LABEL_MAP[billing.type]}
                  </Typography>
                  <Typography fontSize={12} color={theme.palette.grey.medium}>
                    {`#${billing.number}`}
                  </Typography>
                </Box>
              ),
            }}
            cardListContainerProps={{ className: "" }}
            data={billingCommitments}
            loading={isLoading}
          />
        )}
        <PrebillPaymentInformationModal
          open={isPaymentModalOpen}
          attempt={attempt}
          invoice={invoice}
          length={billingCommitments.length}
          onRefresh={getBillingCommitments}
          onClose={handlePaymentModalClose}
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          open={isDeleteModalOpen}
          disableClick={isLoading}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.error.main,
          }}
          textPositive={BUTTON_LABELS.DELETE}
          handleNegative={handleDeleteModalClose}
          handlePositive={() => {
            deleteBillingCommitment(selectedBillingCommitment);
            handleDeleteModalClose();
          }}
          description={`Are you sure you want to delete payment information "${
            PREBILL_PAYMENT_METHOD_LABEL_MAP[selectedBillingCommitment?.type]
          }"?`}
        />
        <DocumentViewer
          documents={[selectedDocument]}
          onUnfullscreen={() => setSelectedDocument(null)}
          fullScreenStatus={selectedDocument}
        />
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.grey.light,
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: theme.shape.borderRadius,
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.fontWeight.semiBold,
  },
  fieldMargin: {
    marginRight: theme.spacing(2),
  },
}));
